import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ModalGlobal } from '../modal/modal.Global';
import { deepCloneArray } from '../../utils/array';
import { getId } from '../../utils/item';
import { update } from '../../services/item';
import { isReadOnly } from '../../utils/roles';
import { resolveIntBound } from './dice.logic';
import {
  SettingsRow,
  SettingsSelect,
  SettingsInput,
  SettingsTextArea,
  WidgetSettingsRoot,
  WidgetSettingsSection,
} from '../form/settings.Widget';
import { BtnSimple } from '../form/btn.Simple';
import { WidgetSettingsCollapsibleSection } from '../form/settings.Widget';
import uuid from 'react-uuid';
import { IconPicker } from '../icons/icon.Picker';
import { PicSelectionOptions } from '../pic/pic.Selector';
import { ColorsSwatch } from '../form/colorsSwatch';

export const DiceSettings = props => {
  const { itemId, diceId, dice, canEdit } = props;
  const item = useSelector(state => state.items?.[itemId]);
  const items = useSelector(state => state.items);

  const canSave = Boolean(canEdit) && isReadOnly() !== true;

  const [label, setLabel] = useState(dice?.label || 'De');
  const [historyLimit, setHistoryLimit] = useState(
    Number.isFinite(dice?.historyLimit) ? dice.historyLimit : 10,
  );
  const [buttons, setButtons] = useState(
    Array.isArray(dice?.buttons) ? dice.buttons : [],
  );
  const buttonsRef = React.useRef(buttons);

  const initialLabel = useMemo(() => dice?.label || 'De', [dice?.label]);

  React.useEffect(() => {
    setLabel(initialLabel);
  }, [initialLabel]);

  React.useEffect(() => {
    setHistoryLimit(
      Number.isFinite(dice?.historyLimit) ? dice.historyLimit : 10,
    );
  }, [dice?.historyLimit]);

  React.useEffect(() => {
    setButtons(Array.isArray(dice?.buttons) ? dice.buttons : []);
  }, [dice?.buttons]);

  React.useEffect(() => {
    buttonsRef.current = buttons;
  }, [buttons]);

  const saveLabel = () => {
    if (!canSave) return;
    const clonedDice = deepCloneArray(item?.dices || []);
    const idx = clonedDice.findIndex(obj => getId(obj) === diceId);
    if (idx === -1) return;
    const existing = clonedDice[idx]?.[diceId] || {};
    clonedDice[idx] = {
      [diceId]: {
        ...existing,
        label: label || 'De',
      },
    };
    update(`items/${itemId}`, 'dices', clonedDice);
  };

  const saveHistoryLimit = () => {
    if (!canSave) return;
    const next = Number.isFinite(+historyLimit)
      ? Math.max(0, Math.min(50, +historyLimit))
      : 10;
    const clonedDice = deepCloneArray(item?.dices || []);
    const idx = clonedDice.findIndex(obj => getId(obj) === diceId);
    if (idx === -1) return;
    const existing = clonedDice[idx]?.[diceId] || {};
    const existingHistory = Array.isArray(existing?.history)
      ? existing.history
      : [];
    clonedDice[idx] = {
      [diceId]: {
        ...existing,
        historyLimit: next,
        history: next > 0 ? existingHistory.slice(0, next) : [],
      },
    };
    update(`items/${itemId}`, 'dices', clonedDice);
  };

  const clearLast = () => {
    if (!canSave) return;
    const clonedDice = deepCloneArray(item?.dices || []);
    const idx = clonedDice.findIndex(obj => getId(obj) === diceId);
    if (idx === -1) return;
    const existing = clonedDice[idx]?.[diceId] || {};
    clonedDice[idx] = {
      [diceId]: {
        ...existing,
        last: null,
      },
    };
    update(`items/${itemId}`, 'dices', clonedDice);
  };

  const clearHistory = () => {
    if (!canSave) return;
    const clonedDice = deepCloneArray(item?.dices || []);
    const idx = clonedDice.findIndex(obj => getId(obj) === diceId);
    if (idx === -1) return;
    const existing = clonedDice[idx]?.[diceId] || {};
    clonedDice[idx] = {
      [diceId]: {
        ...existing,
        history: [],
      },
    };
    update(`items/${itemId}`, 'dices', clonedDice);
  };

  const saveButtons = nextButtons => {
    if (!canSave) return;
    const clonedDice = deepCloneArray(item?.dices || []);
    const idx = clonedDice.findIndex(obj => getId(obj) === diceId);
    if (idx === -1) return;
    const existing = clonedDice[idx]?.[diceId] || {};
    clonedDice[idx] = {
      [diceId]: {
        ...existing,
        buttons: Array.isArray(nextButtons) ? nextButtons : [],
      },
    };
    update(`items/${itemId}`, 'dices', clonedDice);
  };

  const normalizeButtons = arr => {
    if (!Array.isArray(arr)) return [];
    return arr.map(btn => {
      const safe = btn && typeof btn === 'object' ? btn : {};
      return {
        id: safe?.id || uuid().replaceAll('-', ''),
        label: safe?.label ?? 'd6',
        count:
          typeof safe?.count === 'string'
            ? safe.count
            : Number.isFinite(+safe?.count)
              ? +safe.count
              : (safe?.count ?? 1),
        multiDisplay: safe?.multiDisplay === 'each' ? 'each' : 'total',
        resultRules: Array.isArray(safe?.resultRules) ? safe.resultRules : [],
        min: safe?.min ?? '1',
        max: safe?.max ?? '6',
        faces: Array.isArray(safe?.faces) ? safe.faces : [],
      };
    });
  };

  const updateButtonDraft = (index, patch) => {
    const next = normalizeButtons(buttons || []).map((b, i) =>
      i === index ? { ...(b || {}), ...(patch || {}) } : b,
    );
    setButtons(next);
    buttonsRef.current = next;
  };

  const removeButton = index => {
    const next = normalizeButtons(buttons || []).filter((_, i) => i !== index);
    setButtons(next);
    saveButtons(next);
  };

  const addButton = () => {
    const next = [
      ...normalizeButtons(buttons || []),
      {
        id: uuid().replaceAll('-', ''),
        label: 'd6',
        count: 1,
        multiDisplay: 'total',
        resultRules: [],
        min: '1',
        max: '6',
        faces: [],
      },
    ];
    setButtons(next);
    saveButtons(next);
  };

  const normalizeRules = rules => {
    if (!Array.isArray(rules)) return [];
    return rules.map(rule => {
      const safe = rule && typeof rule === 'object' ? rule : {};
      return {
        id: safe?.id || uuid().replaceAll('-', ''),
        label: safe?.label ?? 'resultat',
        iconSlug: safe?.iconSlug ?? null,
        color: safe?.color ?? null,
        priority: Number.isFinite(Number(safe?.priority))
          ? Number(safe.priority)
          : 0,
        description:
          typeof safe?.description === 'string' ? safe.description : '',
        op: safe?.op || '>=',
        threshold:
          typeof safe?.threshold === 'string'
            ? safe.threshold
            : String(safe?.threshold ?? ''),
      };
    });
  };

  const updateRuleDraft = (buttonIndex, ruleIndex, patch) => {
    const nextButtons = normalizeButtons(buttons || []).map((btn, idx) => {
      if (idx !== buttonIndex) return btn;
      const rules = normalizeRules(btn?.resultRules);
      const nextRules = rules.map((r, i) =>
        i === ruleIndex ? { ...(r || {}), ...(patch || {}) } : r,
      );
      return { ...btn, resultRules: nextRules };
    });
    setButtons(nextButtons);
    buttonsRef.current = nextButtons;
  };

  const addRule = buttonIndex => {
    const nextButtons = normalizeButtons(buttons || []).map((btn, idx) => {
      if (idx !== buttonIndex) return btn;
      const rules = normalizeRules(btn?.resultRules);
      return {
        ...btn,
        resultRules: [
          ...rules,
          {
            id: uuid().replaceAll('-', ''),
            label: 'reussite',
            priority: 0,
            op: '>=',
            threshold: '10',
          },
        ],
      };
    });
    setButtons(nextButtons);
    saveButtons(nextButtons);
  };

  const removeRule = (buttonIndex, ruleIndex) => {
    const nextButtons = normalizeButtons(buttons || []).map((btn, idx) => {
      if (idx !== buttonIndex) return btn;
      const rules = normalizeRules(btn?.resultRules);
      return { ...btn, resultRules: rules.filter((_, i) => i !== ruleIndex) };
    });
    setButtons(nextButtons);
    saveButtons(nextButtons);
  };

  const updateFaceDraft = (buttonIndex, faceIndex, patch) => {
    const nextButtons = normalizeButtons(buttons || []).map((btn, idx) => {
      if (idx !== buttonIndex) return btn;
      const faces = Array.isArray(btn.faces) ? btn.faces : [];
      const nextFaces = faces.map((f, i) =>
        i === faceIndex ? { ...(f || {}), ...(patch || {}) } : f,
      );
      return { ...btn, faces: nextFaces };
    });
    setButtons(nextButtons);
    buttonsRef.current = nextButtons;
  };

  const addFace = buttonIndex => {
    const nextButtons = normalizeButtons(buttons || []).map((btn, idx) => {
      if (idx !== buttonIndex) return btn;
      const faces = Array.isArray(btn.faces) ? btn.faces : [];
      return {
        ...btn,
        faces: [
          ...faces,
          { value: 1, type: 'text', text: 'face', iconSlug: null, picId: null },
        ],
      };
    });
    setButtons(nextButtons);
    saveButtons(nextButtons);
  };

  const removeFace = (buttonIndex, faceIndex) => {
    const nextButtons = normalizeButtons(buttons || []).map((btn, idx) => {
      if (idx !== buttonIndex) return btn;
      const faces = Array.isArray(btn.faces) ? btn.faces : [];
      return { ...btn, faces: faces.filter((_, i) => i !== faceIndex) };
    });
    setButtons(nextButtons);
    saveButtons(nextButtons);
  };

  const picOptions = React.useMemo(() => {
    const options = [{ key: 'none', label: 'Aucune image' }];
    (item?.pics || []).forEach((picObj, picIndex) => {
      const picId = getId(picObj);
      const payload = picObj?.[picId] || {};
      options.push({
        key: picId,
        label: payload?.label || `Image ${picIndex + 1}`,
        previewUrl: payload?.url || null,
        iconSlug: payload?.iconSlug || null,
      });
    });
    return options;
  }, [item?.pics]);

  const resolveDraftCount = React.useCallback(
    buttonConfig => {
      const resolved = resolveIntBound(buttonConfig?.count ?? 1, item, items);
      return Number.isFinite(resolved) ? Math.max(1, resolved) : null;
    },
    [item, items],
  );

  return (
    <ModalGlobal
      idToOpen={diceId}
      title='De'
    >
      <WidgetSettingsRoot>
        <WidgetSettingsSection title='general'>
          <SettingsRow label='nom'>
            <SettingsInput
              resetValue={label}
              onChange={e => setLabel(e.target.value)}
              onBlur={saveLabel}
            />
          </SettingsRow>

          <SettingsRow label='historique (X derniers jets)'>
            <SettingsInput
              resetValue={String(historyLimit)}
              onChange={e => setHistoryLimit(e.target.value)}
              onBlur={saveHistoryLimit}
            />
          </SettingsRow>

          <div className='flex gap-05 justify-end'>
            <BtnSimple
              className='text-xs bg-primary'
              onClick={clearLast}
              disabled={!canSave}
            >
              effacer le dernier jet
            </BtnSimple>
            <BtnSimple
              className='text-xs bg-primary'
              onClick={clearHistory}
              disabled={!canSave}
            >
              effacer l'historique
            </BtnSimple>
          </div>
        </WidgetSettingsSection>

        <WidgetSettingsCollapsibleSection
          title='boutons'
          help='Chaque bouton lance un nombre aleatoire entre X et Y. Min, max et nombre de dé peuvent etre des nombres ou une formule (ex: [links.force] + 2 ou [itemId.numId[).'
          defaultOpen={false}
        >
          <div className='flex flex-col gap-05'>
            {(buttons || []).length === 0 && (
              <div className='text-xs text-contrast text-opacity-60'>
                aucun bouton
              </div>
            )}
            {normalizeButtons(buttons || []).map((b, idx) => (
              <div
                key={b?.id || idx}
                className='rounded border border-primary border-opacity-20 bg-main bg-opacity-20 p-05 flex flex-col gap-05'
              >
                {(() => {
                  const resolvedCount = resolveDraftCount(b);
                  const showMultiDisplay =
                    resolvedCount === null ||
                    resolvedCount > 1 ||
                    (typeof b?.count === 'string' && b.count.trim() !== '1');

                  return (
                    <div className='flex flex-col gap-05'>
                  <SettingsRow
                    label='label'
                    dense
                    labelClassName='uppercase text-xs text-opacity-60'
                  >
                    <SettingsInput
                      resetValue={b?.label || ''}
                      onChange={e =>
                        updateButtonDraft(idx, { label: e.target.value })
                      }
                      onBlur={() => saveButtons(buttonsRef.current)}
                    />
                  </SettingsRow>

                  <div className='flex flex-wrap flex-row gap-05'>
                    <div className='flex-1'>
                      <SettingsRow
                        label='min'
                        dense
                        labelClassName='uppercase text-xs text-opacity-60'
                      >
                        <SettingsInput
                          resetValue={String(b?.min ?? '')}
                          onChange={e =>
                            updateButtonDraft(idx, { min: e.target.value })
                          }
                          onBlur={() => saveButtons(buttonsRef.current)}
                        />
                      </SettingsRow>
                    </div>

                    <div className='flex-1'>
                      <SettingsRow
                        label='max'
                        dense
                        labelClassName='uppercase text-xs text-opacity-60'
                      >
                        <SettingsInput
                          resetValue={String(b?.max ?? '')}
                          onChange={e =>
                            updateButtonDraft(idx, { max: e.target.value })
                          }
                          onBlur={() => saveButtons(buttonsRef.current)}
                        />
                      </SettingsRow>
                    </div>

                    <div className='flex-1'>
                      <SettingsRow
                        label='nombre de dé'
                        dense
                        labelClassName='uppercase text-xs text-opacity-60'
                      >
                        <SettingsInput
                          resetValue={String(b?.count ?? 1)}
                          onChange={e =>
                            updateButtonDraft(idx, { count: e.target.value })
                          }
                          onBlur={() => saveButtons(buttonsRef.current)}
                        />
                      </SettingsRow>
                    </div>
                  </div>

                  {showMultiDisplay && (
                    <SettingsRow
                      label='multi'
                      dense
                      labelClassName='uppercase text-xs text-opacity-60'
                    >
                      <SettingsSelect
                        value={b?.multiDisplay || 'total'}
                        onChange={e =>
                          updateButtonDraft(idx, {
                            multiDisplay: e.target.value,
                          })
                        }
                        disabled={!canSave}
                      >
                        <option value='total'>afficher le total</option>
                        <option value='each'>afficher chaque dé</option>
                      </SettingsSelect>
                    </SettingsRow>
                  )}

                  <div className='flex justify-end pt-02'>
                    <BtnSimple
                      className='text-xs bg-primary'
                      onClick={() => removeButton(idx)}
                      disabled={!canSave}
                    >
                      supprimer
                    </BtnSimple>
                  </div>
                    </div>
                  );
                })()}

                <WidgetSettingsCollapsibleSection
                  title='faces'
                  help="Optionnel: remplace l'affichage du numero par un texte, une icone, ou une image."
                  defaultOpen={false}
                  className='mt-05'
                >
                  <div className='flex flex-col gap-05'>
                    {(Array.isArray(b?.faces) ? b.faces : []).length === 0 && (
                      <div className='text-xs text-contrast text-opacity-60'>
                        aucune face customisee
                      </div>
                    )}

                    {(Array.isArray(b?.faces) ? b.faces : []).map(
                      (face, fIdx) => {
                        const faceType = face?.type || 'text';
                        const faceValue = face?.value ?? '';
                        const facePicId = face?.picId || null;

                        return (
                          <div
                            key={`${b.id || idx}-face-${fIdx}`}
                            className='rounded border border-primary border-opacity-20 bg-main bg-opacity-20 p-05 flex flex-col gap-05'
                          >
                            <div className='flex flex-col sm:flex-row gap-05 items-start'>
                              <div className='w-full sm:w-110'>
                                <SettingsRow
                                  label='valeur'
                                  dense
                                  labelClassName='uppercase text-xs text-opacity-60'
                                >
                                  <SettingsInput
                                    resetValue={String(faceValue)}
                                    onChange={e =>
                                      updateFaceDraft(idx, fIdx, {
                                        value: e.target.value,
                                      })
                                    }
                                    onBlur={() =>
                                      saveButtons(buttonsRef.current)
                                    }
                                  />
                                </SettingsRow>
                              </div>

                              <div className='w-full sm:flex-1'>
                                <SettingsRow
                                  label='type'
                                  dense
                                  labelClassName='uppercase text-xs text-opacity-60'
                                >
                                  <SettingsSelect
                                    value={faceType}
                                    onChange={e =>
                                      updateFaceDraft(idx, fIdx, {
                                        type: e.target.value,
                                        text:
                                          e.target.value === 'text'
                                            ? face?.text || ''
                                            : face?.text,
                                        iconSlug:
                                          e.target.value === 'icon'
                                            ? face?.iconSlug || null
                                            : null,
                                        picId:
                                          e.target.value === 'pic'
                                            ? face?.picId || null
                                            : null,
                                      })
                                    }
                                    disabled={!canSave}
                                  >
                                    <option value='text'>texte</option>
                                    <option value='icon'>icone</option>
                                    <option value='pic'>image</option>
                                  </SettingsSelect>
                                </SettingsRow>
                              </div>
                            </div>

                            {faceType === 'text' && (
                              <SettingsRow
                                label='texte'
                                dense
                                labelClassName='uppercase text-xs text-opacity-60'
                              >
                                <SettingsInput
                                  resetValue={face?.text || ''}
                                  onChange={e =>
                                    updateFaceDraft(idx, fIdx, {
                                      text: e.target.value,
                                    })
                                  }
                                  onBlur={() => saveButtons(buttonsRef.current)}
                                />
                              </SettingsRow>
                            )}

                            {faceType === 'icon' && (
                              <div
                                className='board-grid-no-drag'
                                onMouseDown={e => e.stopPropagation()}
                                onPointerDown={e => e.stopPropagation()}
                              >
                                <IconPicker
                                  size={22}
                                  previewSize={22}
                                  maxHeight={120}
                                  value={face?.iconSlug || null}
                                  onChange={(label, slug) => {
                                    updateFaceDraft(idx, fIdx, {
                                      iconSlug: slug,
                                    });
                                    saveButtons(buttonsRef.current);
                                  }}
                                  label='icone'
                                  placeholder='Rechercher une icone'
                                  showPreview={true}
                                />
                              </div>
                            )}

                            {faceType === 'pic' && (
                              <div
                                className='board-grid-no-drag'
                                onMouseDown={e => e.stopPropagation()}
                                onPointerDown={e => e.stopPropagation()}
                              >
                                <PicSelectionOptions
                                  title='image'
                                  options={picOptions}
                                  value={facePicId || 'none'}
                                  size={26}
                                  onChange={key => {
                                    updateFaceDraft(idx, fIdx, {
                                      picId: key === 'none' ? null : key,
                                    });
                                    saveButtons(buttonsRef.current);
                                  }}
                                />
                              </div>
                            )}

                            <div className='flex justify-end'>
                              <BtnSimple
                                className='text-xs bg-primary'
                                onClick={() => removeFace(idx, fIdx)}
                                disabled={!canSave}
                              >
                                supprimer la face
                              </BtnSimple>
                            </div>
                          </div>
                        );
                      },
                    )}

                    <div className='flex justify-end'>
                      <BtnSimple
                        className='text-xs bg-primary'
                        onClick={() => addFace(idx)}
                        disabled={!canSave}
                      >
                        ajouter une face
                      </BtnSimple>
                    </div>
                  </div>
                </WidgetSettingsCollapsibleSection>

                <WidgetSettingsCollapsibleSection
                  title='resultats'
                  help="Optionnel: definit le type de resultat (ex: reussite/echec) en fonction d'un seuil calcule depuis l'item qui lance le de."
                  defaultOpen={false}
                  className='mt-05'
                >
                  <div className='flex flex-col gap-05'>
                    {normalizeRules(b?.resultRules).length === 0 && (
                      <div className='text-xs text-contrast text-opacity-60'>
                        aucune regle
                      </div>
                    )}

                    {normalizeRules(b?.resultRules).map((rule, rIdx) => (
                      <div
                        key={rule.id || rIdx}
                        className='rounded border border-primary border-opacity-20 bg-main bg-opacity-20 p-05 flex flex-col gap-05'
                      >
                        <SettingsRow
                          label='label'
                          dense
                          labelClassName='uppercase text-xs text-opacity-60'
                        >
                          <SettingsInput
                            resetValue={rule?.label || ''}
                            onChange={e =>
                              updateRuleDraft(idx, rIdx, {
                                label: e.target.value,
                              })
                            }
                            onBlur={() => saveButtons(buttonsRef.current)}
                          />
                        </SettingsRow>

                        <SettingsRow
                          label='priorité'
                          dense
                          labelClassName='uppercase text-xs text-opacity-60'
                        >
                          <SettingsInput
                            type='number'
                            resetValue={String(rule?.priority ?? 0)}
                            onChange={e =>
                              updateRuleDraft(idx, rIdx, {
                                priority: e.target.value,
                              })
                            }
                            onBlur={() => saveButtons(buttonsRef.current)}
                          />
                        </SettingsRow>

                        <SettingsRow
                          label='description'
                          dense
                          labelClassName='uppercase text-xs text-opacity-60'
                        >
                          <div
                            className='board-grid-no-drag w-full flex'
                            onMouseDown={e => e.stopPropagation()}
                            onPointerDown={e => e.stopPropagation()}
                          >
                            <SettingsTextArea
                              resetValue={rule?.description || ''}
                              onChange={e =>
                                updateRuleDraft(idx, rIdx, {
                                  description: e.target.value,
                                })
                              }
                              onBlur={() => saveButtons(buttonsRef.current)}
                              rows={3}
                            />
                          </div>
                        </SettingsRow>

                        <div className='flex flex-col sm:flex-row gap-05'>
                          <div
                            className='flex-1 board-grid-no-drag'
                            onMouseDown={e => e.stopPropagation()}
                            onPointerDown={e => e.stopPropagation()}
                          >
                            <IconPicker
                              size={20}
                              previewSize={20}
                              maxHeight={90}
                              value={rule?.iconSlug || null}
                              onChange={(label, slug) => {
                                updateRuleDraft(idx, rIdx, { iconSlug: slug });
                                saveButtons(buttonsRef.current);
                              }}
                              label='icone'
                              placeholder='Rechercher une icone'
                              showPreview={true}
                            />
                          </div>

                          <div className='flex-1'>
                            <SettingsRow
                              label='couleur'
                              dense
                              labelClassName='uppercase text-xs text-opacity-60'
                            >
                              <div
                                className='board-grid-no-drag'
                                onMouseDown={e => e.stopPropagation()}
                                onPointerDown={e => e.stopPropagation()}
                              >
                                <ColorsSwatch
                                  currentColor={rule?.color || 'transparent'}
                                  colors={[
                                    'transparent',
                                    'primary',
                                    'secondary-d',
                                    'red',
                                    'orange',
                                    'yellow',
                                    'lime',
                                    'green',
                                    'blue',
                                    'purple',
                                    'pink',
                                  ]}
                                  onClick={color => {
                                    updateRuleDraft(idx, rIdx, { color });
                                    saveButtons(buttonsRef.current);
                                  }}
                                />
                              </div>
                            </SettingsRow>
                          </div>
                        </div>

                        <div className='flex flex-col sm:flex-row gap-05'>
                          <div className='flex-1'>
                            <SettingsRow
                              label='condition'
                              dense
                              labelClassName='uppercase text-xs text-opacity-60'
                            >
                              <SettingsSelect
                                value={rule?.op || '>='}
                                onChange={e =>
                                  updateRuleDraft(idx, rIdx, {
                                    op: e.target.value,
                                  })
                                }
                                disabled={!canSave}
                              >
                                <option value='>='>{'>='}</option>
                                <option value='>'>{'>'}</option>
                                <option value='=='>{'=='}</option>
                                <option value='!='>{'!='}</option>
                                <option value='<'>{'<'}</option>
                                <option value='<='>{'<='}</option>
                                <option value='default'>sinon (defaut)</option>
                              </SettingsSelect>
                            </SettingsRow>
                          </div>

                          {rule?.op !== 'default' && (
                            <div className='flex-1'>
                              <SettingsRow
                                label='seuil'
                                dense
                                labelClassName='uppercase text-xs text-opacity-60'
                              >
                                <SettingsInput
                                  resetValue={rule?.threshold || ''}
                                  onChange={e =>
                                    updateRuleDraft(idx, rIdx, {
                                      threshold: e.target.value,
                                    })
                                  }
                                  onBlur={() => saveButtons(buttonsRef.current)}
                                />
                              </SettingsRow>
                            </div>
                          )}
                        </div>

                        <div className='flex justify-end'>
                          <BtnSimple
                            className='text-xs bg-primary'
                            onClick={() => removeRule(idx, rIdx)}
                            disabled={!canSave}
                          >
                            supprimer la regle
                          </BtnSimple>
                        </div>
                      </div>
                    ))}

                    <div className='flex justify-end'>
                      <BtnSimple
                        className='text-xs bg-primary'
                        onClick={() => addRule(idx)}
                        disabled={!canSave}
                      >
                        ajouter une regle
                      </BtnSimple>
                    </div>
                  </div>
                </WidgetSettingsCollapsibleSection>
              </div>
            ))}

            <div className='flex justify-end'>
              <BtnSimple
                className='text-xs bg-primary'
                onClick={addButton}
                disabled={!canSave}
              >
                ajouter un bouton
              </BtnSimple>
            </div>
          </div>
        </WidgetSettingsCollapsibleSection>
      </WidgetSettingsRoot>
    </ModalGlobal>
  );
};
